import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useGiftCsMemosByOrders } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import { GiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { GiftDeliverySummary } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  giftOrderSearchDataAtomFamily,
  giftOrderSearchStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import {
  GiftOrderSearchParams,
  useGiftOrderSearchHook,
} from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderDocStatus,
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Link } from 'react-router-dom';

import { CouponDetailModal } from './CouponDetailModal';
import { CouponFinderPanel } from './CouponFinderPanel';
import {
  computeCrossCouponHashOverlap,
  computeCrossOrderHashOverlap,
  CrossOrderHashOverlapGroup,
} from './couponDetail/kakaoShareOverlap';
import { PhoneFormUploadModal } from './couponDetail/PhoneFormUploadModal';
import { CrossOrderHashOverlapBanner } from './CrossOrderHashOverlapBanner';
import { CsMemoButton } from './csMemo';
import { DeliveryDetailModal } from './DeliveryDetailModal';
import { DeliveryStatsCell } from './OrderRow';
import { ReceiptLinkButton } from './ReceiptLinkButton';
import {
  buildGiftItemEmbedMap,
  combinedOrderLabel,
  exportCombinedPhoneFormExcel,
  getPhoneFormTargets,
  sortOrdersByPurchasedAt,
} from '../utils/deliveredCouponPhoneExcel';
import { formatDateTimeCompact } from '../utils/giftOrder.constants';
import {
  parsePhoneFormWorkbook,
  PhoneFormParseResult,
} from '../utils/phoneFormExcelUpload';

const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

type PaymentStatusFilter = 'all' | GiftOrderPaymentStatus;

const paymentStatusFilterOptions: PaymentStatusFilter[] = [
  'all',
  GiftOrderPaymentStatus.Paid,
  GiftOrderPaymentStatus.Confirmed,
  GiftOrderPaymentStatus.Waiting,
  GiftOrderPaymentStatus.NotPaid,
  GiftOrderPaymentStatus.CancelRequested,
  GiftOrderPaymentStatus.Canceled,
];

const getFilterLabel = (filter: PaymentStatusFilter) =>
  filter === 'all' ? '전체' : paymentStatusLabel[filter];

const getCouponStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  const activeCoupons = coupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
      c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
  );
  const activeCount = activeCoupons.length;
  const unusedCount = activeCoupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
      c.redeemStatus !== GiftCouponRedeemStatus.Refunded,
  ).length;
  return { unusedCount, totalCount: activeCount };
};

const getKakaoRiskInfo = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) {
    return { multiRoomCount: 0, crossCouponGroupCount: 0, crossCouponCouponCount: 0 };
  }
  const multiRoomCount = coupons.filter((c) => (c.shareKakaoCount ?? 0) >= 2).length;
  const overlap = computeCrossCouponHashOverlap(coupons);
  return {
    multiRoomCount,
    crossCouponGroupCount: overlap.groups.length,
    crossCouponCouponCount: overlap.overlapCouponIds.size,
  };
};

const OrderRow: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (order: GiftOrderForAdmin) => void;
  onDeliveryClick: (order: GiftOrderForAdmin) => void;
  crossOrderGroups?: CrossOrderHashOverlapGroup[];
  /** 이 주문의 CS 응대 메모 (쿠폰·배송 단위 포함) */
  csMemos?: GiftCsMemo[];
  csMemoLoading?: boolean;
  csMemoUnavailable?: boolean;
  /** 찾아 들어온 주문 (쿠폰 → 주문검색) */
  isHighlighted?: boolean;
  highlightLabel?: string;
  /** CS메모 칩에 마지막 메모 미리보기 노출 (좁은 모달에선 끈다 — 개수만) */
  csMemoPreview?: boolean;
}> = ({
  order,
  onCouponClick,
  onDeliveryClick,
  crossOrderGroups,
  csMemos,
  csMemoLoading,
  csMemoUnavailable,
  isHighlighted,
  highlightLabel,
  csMemoPreview,
}) => {
  // 주문자 — 결제 시점 구매자가 입력한 이름/연락처 (동결 스냅샷)
  const ordererName = order.receiverName || '-';
  const ordererPhone = order.receiverPhoneNumber || '';
  // 부고 — 부고에 연결된 회원(상주)
  const memberName = order.bugo?.member?.fullName || '-';
  const memberPhone = order.bugo?.member?.phoneNumber || '-';
  const deceasedName = order.bugo?.fevent?.deceasedInfo?.name || '-';
  const bugoBrandName =
    typeof order.bugoBrand === 'object' ? order.bugoBrand.bugoAgency?.corpName : '-';
  const totalItems = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;
  const isDeleted = order.docStatus === GiftOrderDocStatus.Deleted;
  const couponStats = getCouponStats(order);
  const kakaoRisk = getKakaoRiskInfo(order);

  const crossOrderRoomCount = crossOrderGroups?.length ?? 0;
  const crossOrderOtherOrderIds = (() => {
    if (!crossOrderGroups || crossOrderGroups.length === 0) return [] as string[];
    const set = new Set<string>();
    for (const g of crossOrderGroups) {
      for (const oid of g.orderIds) {
        if (oid !== order._id) set.add(oid);
      }
    }
    return Array.from(set);
  })();

  // 통합 타임라인에서 "이 메모가 어느 쿠폰/배송 건인지" 라벨
  const csMemoSubLabelMap = (() => {
    const map = new Map<string, string>();
    for (const c of order.coupons ?? []) map.set(c._id, `쿠폰 ${c.code}-${c.seq}`);
    for (const d of (order.deliveries ?? []) as GiftDeliverySummary[]) {
      map.set(d._id, `배송 ${d.code}`);
    }
    return map;
  })();

  const hasKakaoRisk =
    kakaoRisk.multiRoomCount > 0 ||
    kakaoRisk.crossCouponGroupCount > 0 ||
    crossOrderRoomCount > 0;
  const hasCrossOrderRisk = crossOrderRoomCount > 0;

  return (
    <tr
      id={`gift-order-row-${order._id}`}
      className={`hover:bg-gray-50 ${
        isDeleted
          ? 'bg-red-50'
          : hasCrossOrderRisk
          ? 'bg-red-50/40 ring-1 ring-inset ring-red-200'
          : ''
      } ${isHighlighted ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-400' : ''}`}
    >
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {isHighlighted && (
          <span
            className="mb-1 inline-flex items-center rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white"
            title={
              highlightLabel
                ? `쿠폰 ${highlightLabel} — 이 쿠폰이 들어있는 주문`
                : '찾던 쿠폰이 들어있는 주문'
            }
          >
            찾는 쿠폰{highlightLabel ? ` ${highlightLabel}` : ''}
          </span>
        )}
        <div className="flex items-center gap-1">
          <span>
            {formatDateTimeCompact(order.paymentDetail.purchasedAt || order.createdAt)}
          </span>
          <button
            type="button"
            className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5 text-[10px] text-gray-500 hover:bg-gray-200"
            title={`주문 _id 복사 (${order._id})`}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await navigator.clipboard.writeText(order._id);
                toastSuccess('주문 _id가 복사되었습니다.');
              } catch {
                toastError('복사에 실패했습니다.');
              }
            }}
          >
            ID복사
          </button>
          <ReceiptLinkButton receiptUrl={order.paymentDetail.receiptUrl} />
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <Link
          to={`/gift-seller/order/${order._id}`}
          className="font-medium text-indigo-600 hover:text-indigo-900"
        >
          {orderName}
        </Link>
        <p className="text-xs text-gray-400">수량: {totalItems}개</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
        {formatPrice(order.paymentDetail.paymentByCash)}원
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {order.paymentDetail.method || '-'}
      </td>
      {/* 주문자 (구매자 입력값) */}
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{ordererName}</p>
        {ordererPhone && (
          <p className="text-xs">{phoneNumberStringFormator(ordererPhone)}</p>
        )}
      </td>
      {/* 부고 (연결된 상주 회원) */}
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{memberName}</p>
        <p className="text-xs">{phoneNumberStringFormator(memberPhone)}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {deceasedName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {bugoBrandName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        {couponStats && order.coupons && order.coupons.length > 0 ? (
          <div className="flex flex-col items-start gap-1">
            <button
              onClick={() => onCouponClick(order)}
              className={`inline-flex cursor-pointer rounded-full px-2 text-xs font-semibold leading-5 transition-opacity hover:opacity-75 ${
                couponStats.unusedCount === 0
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {couponStats.unusedCount}/{couponStats.totalCount}
            </button>
            {hasKakaoRisk && (
              <div className="flex flex-wrap items-center gap-1">
                {kakaoRisk.multiRoomCount > 0 && (
                  <button
                    onClick={() => onCouponClick(order)}
                    className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-200"
                    title={`중복노출 — 이 주문에서 ${kakaoRisk.multiRoomCount}개 쿠폰이 2개 이상 서로 다른 카톡방으로 전달됨`}
                  >
                    <span>⚠</span>
                    <span>중복노출 {kakaoRisk.multiRoomCount}</span>
                  </button>
                )}
                {kakaoRisk.crossCouponGroupCount > 0 && (
                  <button
                    onClick={() => onCouponClick(order)}
                    className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-200"
                    title={`다건수신 (주문내) — 이 주문의 ${kakaoRisk.crossCouponCouponCount}개 쿠폰이 동일한 카톡방으로 함께 수신됨 (${kakaoRisk.crossCouponGroupCount}개 채팅방)`}
                  >
                    <span>⚠</span>
                    <span>다건수신(주문내) {kakaoRisk.crossCouponCouponCount}</span>
                  </button>
                )}
                {hasCrossOrderRisk && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white ring-1 ring-red-700"
                    title={`다건수신 (주문간) — 다른 ${crossOrderOtherOrderIds.length}개 주문의 쿠폰과 동일한 카톡방으로 함께 수신됨 (${crossOrderRoomCount}개 채팅방). 상단 배너 참고.`}
                  >
                    <span>⚠</span>
                    <span>다건수신(주문간) {crossOrderOtherOrderIds.length}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <DeliveryStatsCell order={order} onDeliveryClick={() => onDeliveryClick(order)} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <span
          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
            order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
            order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed
              ? 'bg-green-100 text-green-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.Canceled
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {paymentStatusLabel[order.paymentDetail.status] || order.paymentDetail.status}
        </span>
        {isDeleted && (
          <span className="ml-1 inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
            삭제됨
          </span>
        )}
      </td>
      {/* CS 응대 메모 — 클릭하면 이 주문의 메모장(쿠폰·배송 메모 포함) */}
      <td className="px-3 py-4 text-sm">
        <CsMemoButton
          target={{ giftOrder: order._id }}
          title="주문 CS 응대 메모"
          subtitle={`주문자 ${ordererName} · ${
            ordererPhone ? phoneNumberStringFormator(ordererPhone) : '-'
          }`}
          memos={csMemos}
          isLoading={csMemoLoading}
          unavailable={csMemoUnavailable}
          subLabelMap={csMemoSubLabelMap}
          showPreview={csMemoPreview}
          notice="주문·쿠폰·배송 메모 통합 · 입력은 주문 단위로 저장"
        />
      </td>
    </tr>
  );
};

interface OrderSearchResultsViewProps {
  /** null 이면 검색을 트리거하지 않고 안내 빈상태를 렌더 */
  searchParams: GiftOrderSearchParams | null;
  /** false면 검색 전 안내 UI 표시 (페이지에서만 사용) */
  hasSearched?: boolean;
  /** 검색 전 안내 UI를 부모가 직접 그릴 경우 false */
  renderEmptyState?: boolean;
  /**
   * 강조할 주문 — 쿠폰 내역 검색에서 "이 쿠폰이 들어있는 주문"으로 들어온 경우.
   * 결과에 있으면 행을 강조하고 자동으로 그 위치까지 스크롤한다.
   */
  highlightOrderId?: string;
  /** 강조 이유 라벨 (예: 17DA5C-5) */
  highlightLabel?: string;
  /**
   * 결제상태 칩의 초기값 (기본 '전체').
   * 쿠폰에서 들어온 조회는 미결제 주문을 볼 이유가 없어 '결제완료'로 연다.
   */
  defaultPaymentStatusFilter?: PaymentStatusFilter;
  /**
   * CS메모 칩에 마지막 메모 미리보기를 붙일지 (기본 true).
   * 모달처럼 폭이 빠듯한 곳에서는 꺼서 개수 배지만 두고 클릭해서 보게 한다.
   */
  csMemoPreview?: boolean;
}

export const OrderSearchResultsView: FC<OrderSearchResultsViewProps> = ({
  searchParams,
  hasSearched = true,
  renderEmptyState = true,
  highlightOrderId,
  highlightLabel,
  defaultPaymentStatusFilter = 'all',
  csMemoPreview = true,
}) => {
  const [modalCouponOrder, setModalCouponOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );
  // 쿠폰 찾기에서 연 경우 그 쿠폰 행을 강조·스크롤 (주문 쿠폰 배지로 연 경우엔 없음)
  const [modalHighlightCouponId, setModalHighlightCouponId] = useState<string | null>(
    null,
  );
  const [modalDeliveryOrder, setModalDeliveryOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatusFilter>(
    defaultPaymentStatusFilter,
  );

  const searchKey = useMemo(
    () => (searchParams ? JSON.stringify(searchParams) : ''),
    [searchParams],
  );

  useGiftOrderSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftOrderSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftOrderSearchDataAtomFamily(searchKey));

  const sortedResults = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      const dateA = new Date(a.paymentDetail.purchasedAt || a.createdAt).getTime();
      const dateB = new Date(b.paymentDetail.purchasedAt || b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [searchResults]);

  const paymentStatusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: sortedResults.length };
    Object.values(GiftOrderPaymentStatus).forEach((s) => {
      counts[s] = 0;
    });
    sortedResults.forEach((order) => {
      const s = order.paymentDetail.status;
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [sortedResults]);

  const results = useMemo(() => {
    if (paymentStatusFilter === 'all') return sortedResults;
    return sortedResults.filter(
      (order) => order.paymentDetail.status === paymentStatusFilter,
    );
  }, [sortedResults, paymentStatusFilter]);

  const crossOrderOverlap = useMemo(
    () => computeCrossOrderHashOverlap(sortedResults),
    [sortedResults],
  );

  // CS 응대 메모 — 검색된 주문들의 메모를 한 번에 채운다 (주문 축 조회라 쿠폰·배송 메모 포함).
  // 백엔드 미배포면 unavailable로 떨어져 배지만 비활성 상태가 된다.
  const csMemoOrderIds = useMemo(() => sortedResults.map((o) => o._id), [sortedResults]);
  const csMemos = useGiftCsMemosByOrders(csMemoOrderIds, status !== 'loading');

  // ── 통합 번호 기입폼 — 한 주문자가 여러 번 주문한 경우 결제완료(확정 포함) 주문들을
  // "주문" 구분 열과 함께 엑셀 한 개로 합쳐 내려주고, 채워 받은 파일도 여기서 한 번에
  // 업로드한다 (주문별로 쪼갤 필요 없음). 검색 필터 칩과 무관하게 대상을 고정 산출.
  const { isAdmin, isGiftAgent } = useAuth();
  const combinedFormOrders = useMemo(
    () =>
      sortOrdersByPurchasedAt(
        sortedResults.filter(
          (o) =>
            o.docStatus !== GiftOrderDocStatus.Deleted &&
            (o.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
              o.paymentDetail.status === GiftOrderPaymentStatus.Confirmed) &&
            getPhoneFormTargets(o.coupons ?? []).length > 0,
        ),
      ),
    [sortedResults],
  );
  // 주문 1건이면 쿠폰 상세의 주문별 기입폼과 같으므로 2건부터 노출
  const canCombinedDownload =
    (isAdmin() || isGiftAgent()) && combinedFormOrders.length >= 2;
  // 저장 엔드포인트가 admin 전용(PATCH /admin/gift-coupon/:id)이라 업로드는 admin만
  const canCombinedUpload = isAdmin() && combinedFormOrders.length >= 2;

  const combinedUploadData = useMemo(() => {
    const embedMap = buildGiftItemEmbedMap(combinedFormOrders);
    const orderLabelByCouponId = new Map<string, string>();
    const coupons: GiftCouponSummary[] = [];
    combinedFormOrders.forEach((o, i) => {
      const label = combinedOrderLabel(o, i);
      for (const c of o.coupons ?? []) {
        coupons.push(c);
        orderLabelByCouponId.set(c._id, label);
      }
    });
    return { embedMap, orderLabelByCouponId, coupons };
  }, [combinedFormOrders]);

  const combinedUploadRef = useRef<HTMLInputElement>(null);
  const [combinedParsed, setCombinedParsed] = useState<PhoneFormParseResult | null>(null);
  const handleCombinedFormFile = useCallback(
    async (file: File) => {
      // 쿠폰 code는 전역 유일이 전제 — 만에 하나 주문 간 code가 겹치면 오배정
      // 위험이 있으므로 통합 업로드를 막고 주문별 업로드로 유도한다.
      const seen = new Set<string>();
      for (const c of combinedUploadData.coupons) {
        if (seen.has(c.code)) {
          toastError(
            '주문 간 쿠폰번호가 겹쳐 통합 업로드를 사용할 수 없습니다 — 각 주문의 기입폼 업로드를 이용해주세요.',
          );
          return;
        }
        seen.add(c.code);
      }
      try {
        const buf = await file.arrayBuffer();
        setCombinedParsed(parsePhoneFormWorkbook(buf, combinedUploadData.coupons));
      } catch (err) {
        toastError(err instanceof Error ? err.message : '엑셀 파일을 읽지 못했습니다.');
      }
    },
    [combinedUploadData],
  );

  const orderMap = useMemo(() => {
    const map = new Map<string, GiftOrderForAdmin>();
    for (const o of sortedResults) map.set(o._id, o);
    return map;
  }, [sortedResults]);

  const handleOpenCouponFromFinder = useCallback(
    (order: GiftOrderForAdmin, couponId: string) => {
      setModalHighlightCouponId(couponId);
      setModalCouponOrder(order);
    },
    [],
  );

  const handleSelectOrder = useCallback((orderId: string) => {
    const el = document.getElementById(`gift-order-row-${orderId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('animate-pulse');
    window.setTimeout(() => el.classList.remove('animate-pulse'), 1500);
  }, []);

  // 강조 대상이 초기 필터(결제완료)에 안 걸리는 상태(확정·취소 등)면 그 주문의 상태로 한 번 맞춘다.
  // 한 번만 — 이후 사용자가 직접 고른 필터를 되돌리면 안 된다.
  const autoFilterFixedRef = useRef(false);
  useEffect(() => {
    if (!highlightOrderId || autoFilterFixedRef.current) return;
    if (results.some((o) => o._id === highlightOrderId)) return;
    const target = sortedResults.find((o) => o._id === highlightOrderId);
    if (!target) return;
    autoFilterFixedRef.current = true;
    setPaymentStatusFilter(target.paymentDetail.status);
  }, [highlightOrderId, results, sortedResults]);

  // 쿠폰 내역 검색에서 "이 쿠폰의 주문"으로 들어온 경우, 결과에 있으면 그 행까지 한 번 스크롤한다.
  // (결과가 16건씩 되면 강조만으로는 화면 밖이라 안 보인다)
  const scrolledHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightOrderId || status === 'loading') return;
    if (scrolledHighlightRef.current === highlightOrderId) return;
    if (!results.some((o) => o._id === highlightOrderId)) return;
    scrolledHighlightRef.current = highlightOrderId;
    // 행이 그려진 뒤에 잡아야 한다 (이 시점엔 아직 DOM에 없을 수 있음)
    const timer = window.setTimeout(() => handleSelectOrder(highlightOrderId), 0);
    return () => window.clearTimeout(timer);
  }, [highlightOrderId, results, status, handleSelectOrder]);

  if (!hasSearched) {
    if (!renderEmptyState) return null;
    return (
      <div className="rounded-lg bg-white p-12 text-center shadow">
        <h3 className="text-sm font-medium text-gray-900">주문 검색</h3>
        <p className="mt-1 text-sm text-gray-500">
          검색 조건을 입력하고 검색 버튼을 눌러주세요.
        </p>
      </div>
    );
  }

  return (
    <>
      {status !== 'loading' && (
        <CrossOrderHashOverlapBanner
          overlap={crossOrderOverlap}
          orderMap={orderMap}
          onSelectOrder={handleSelectOrder}
        />
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow">
        {status === 'loading' ? (
          <div className="center-box py-12">
            <Loading />
          </div>
        ) : (
          <>
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-500">
                  검색 결과: {results.length}건
                  {paymentStatusFilter !== 'all' && (
                    <span className="ml-1 text-gray-400">
                      (전체 {sortedResults.length}건 중)
                    </span>
                  )}
                </p>
                {(canCombinedDownload || canCombinedUpload) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {canCombinedDownload && (
                      <button
                        type="button"
                        onClick={() => exportCombinedPhoneFormExcel(combinedFormOrders)}
                        className="rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
                        title="검색된 결제완료(확정 포함) 주문들의 번호 기입폼을 '주문' 구분 열과 함께 엑셀 한 개로 합쳐 내보내기 — 주문별 빈 행 구분·주문 열 병합, 상품명·판매가 포함, 미발송 건은 '▶ 번호기입요망' 표시"
                      >
                        번호 기입폼 통합 엑셀 ({combinedFormOrders.length}개 주문)
                      </button>
                    )}
                    {canCombinedUpload && (
                      <button
                        type="button"
                        onClick={() => combinedUploadRef.current?.click()}
                        className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                        title="채워 받은 통합 기입폼을 주문별로 쪼개지 않고 한 번에 업로드 — 수령번호는 확인번호로, 메모는 쿠폰 메모로 일괄 저장 (미리보기 후 반영·되돌리기 가능)"
                      >
                        통합 기입폼 업로드
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {paymentStatusFilterOptions.map((option) => {
                  const count = paymentStatusCounts[option] || 0;
                  const isActive = paymentStatusFilter === option;
                  const isDisabled = option !== 'all' && count === 0;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setPaymentStatusFilter(option)}
                      disabled={isDisabled}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-indigo-600 text-white'
                          : isDisabled
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span>{getFilterLabel(option)}</span>
                      <span
                        className={`rounded-full px-1.5 text-[10px] font-semibold ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      결제일시
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상품명
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      결제금액
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      결제방법
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      주문자
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      부고
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      고인명
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상조
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      쿠폰
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div>배송</div>
                      <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                        전체/대기/준비/배송/완료
                      </div>
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상태
                    </th>
                    <th
                      className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      title="CS 응대 메모 (클릭 시 메모장)"
                    >
                      CS메모
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {results.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-3 py-8 text-center text-gray-500">
                        {sortedResults.length === 0
                          ? '검색 결과가 없습니다.'
                          : `'${getFilterLabel(
                              paymentStatusFilter,
                            )}' 상태의 주문이 없습니다.`}
                      </td>
                    </tr>
                  ) : (
                    results.map((order) => (
                      <OrderRow
                        key={order._id}
                        order={order}
                        onCouponClick={(o) => {
                          setModalHighlightCouponId(null);
                          setModalCouponOrder(o);
                        }}
                        onDeliveryClick={setModalDeliveryOrder}
                        crossOrderGroups={crossOrderOverlap.groupsByOrderId.get(
                          order._id,
                        )}
                        csMemos={csMemos.memoMap.get(order._id)}
                        csMemoLoading={csMemos.isLoading}
                        csMemoUnavailable={csMemos.unavailable}
                        isHighlighted={
                          !!highlightOrderId && order._id === highlightOrderId
                        }
                        highlightLabel={highlightLabel}
                        csMemoPreview={csMemoPreview}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {status !== 'loading' && (
        <CouponFinderPanel
          orders={sortedResults}
          onOpenCoupon={handleOpenCouponFromFinder}
          onFocusOrder={handleSelectOrder}
          compact={!csMemoPreview}
        />
      )}

      {modalCouponOrder && modalCouponOrder.coupons && (
        <CouponDetailModal
          coupons={modalCouponOrder.coupons}
          orderId={modalCouponOrder._id}
          order={modalCouponOrder}
          highlightCouponId={modalHighlightCouponId ?? undefined}
          onClose={() => {
            setModalCouponOrder(null);
            setModalHighlightCouponId(null);
          }}
        />
      )}

      {modalDeliveryOrder && modalDeliveryOrder.deliveries && (
        <DeliveryDetailModal
          deliveries={modalDeliveryOrder.deliveries as GiftDeliverySummary[]}
          orderId={modalDeliveryOrder._id}
          order={modalDeliveryOrder}
          onClose={() => setModalDeliveryOrder(null)}
        />
      )}

      {/* 통합 기입폼 파일 선택 (숨김) — 같은 파일 재선택 가능하게 value 초기화 */}
      <input
        ref={combinedUploadRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleCombinedFormFile(file);
        }}
      />

      {combinedParsed && (
        <PhoneFormUploadModal
          parsed={combinedParsed}
          giftItemEmbedMap={combinedUploadData.embedMap}
          orderLabelByCouponId={combinedUploadData.orderLabelByCouponId}
          onClose={(didApply) => {
            setCombinedParsed(null);
            // 검색 결과는 스냅샷이라 저장분 반영은 새로고침으로 (주문별 업로드와 동일)
            if (didApply) window.location.reload();
          }}
        />
      )}
    </>
  );
};
